if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        this.variantIdInput.disabled = false;

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute(
            'aria-haspopup',
            'dialog'
          );
        }

        this.hideErrors =
          this.dataset.hideErrors === 'true';

        /*
         * DJ quantity-limit validation.
         *
         * The quantity selector can live outside the actual <form>
         * element and use form="product-form-...".
         *
         * form.elements still gives us access to controls associated
         * with this form, including those using the form attribute.
         */
        const quantityInput = this.quantityInput;

        if (quantityInput) {
          quantityInput.addEventListener(
            'input',
            this.onQuantityInput.bind(this)
          );
        }

        this.quantityLimitErrorShown = false;
      }

      /*
       * ==========================================================
       * PRODUCT FORM SUBMIT
       * ==========================================================
       */
      onSubmitHandler(evt) {
        evt.preventDefault();

        /*
         * Dawn / product-info.js may already have disabled
         * this button because:
         *
         * - variant is unavailable
         * - product is sold out
         * - custom quantity maximum has already been reached
         */
        if (
          this.submitButton.getAttribute(
            'aria-disabled'
          ) === 'true'
        ) {
          return;
        }

        /*
         * Clear any previous product-form error first.
         */
        this.handleErrorMessage();
        this.quantityLimitErrorShown = false;

        /*
         * ========================================================
         * DJ FINAL QUANTITY VALIDATION
         * ========================================================
         *
         * This is intentionally done BEFORE FormData is created
         * and BEFORE Shopify receives /cart/add.js.
         *
         * HTML max="" by itself does not prevent an Ajax form
         * submission containing a manually entered quantity.
         */
        if (!this.validateQuantityLimit()) {
          return;
        }

        /*
         * From this point forward the requested quantity has
         * passed our client-side limit validation.
         */
        this.submitButton.setAttribute(
          'aria-disabled',
          true
        );

        this.submitButton.classList.add(
          'loading'
        );

        this.querySelector(
          '.loading__spinner'
        )?.classList.remove('hidden');

        const config =
          fetchConfig('javascript');

        config.headers['X-Requested-With'] =
          'XMLHttpRequest';

        delete config.headers['Content-Type'];

        const formData =
          new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart
              .getSectionsToRender()
              .map((section) => section.id)
          );

          formData.append(
            'sections_url',
            window.location.pathname
          );

          this.cart.setActiveElement(
            document.activeElement
          );
        }

        config.body = formData;

        fetch(
          `${routes.cart_add_url}`,
          config
        )
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(
                PUB_SUB_EVENTS.cartError,
                {
                  source: 'product-form',

                  productVariantId:
                    formData.get('id'),

                  errors:
                    response.errors ||
                    response.description,

                  message:
                    response.message,
                }
              );

              this.handleErrorMessage(
                response.description
              );

              const soldOutMessage =
                this.submitButton.querySelector(
                  '.sold-out-message'
                );

              if (!soldOutMessage) {
                return;
              }

              this.submitButton.setAttribute(
                'aria-disabled',
                true
              );

              this.submitButtonText.classList.add(
                'hidden'
              );

              soldOutMessage.classList.remove(
                'hidden'
              );

              this.error = true;

              return;
            } else if (!this.cart) {
              window.location =
                window.routes.cart_url;

              return;
            }

            if (!this.error) {
              publish(
                PUB_SUB_EVENTS.cartUpdate,
                {
                  source: 'product-form',

                  productVariantId:
                    formData.get('id'),

                  cartData: response,
                }
              );
            }

            this.error = false;

            const quickAddModal =
              this.closest(
                'quick-add-modal'
              );

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(
                      response
                    );
                  });
                },
                {
                  once: true,
                }
              );

              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(
                response
              );
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove(
              'loading'
            );

            if (
              this.cart &&
              this.cart.classList.contains(
                'is-empty'
              )
            ) {
              this.cart.classList.remove(
                'is-empty'
              );
            }

            /*
             * Do not undo a quantity-limit disabled state
             * that product-info.js may have applied after
             * the cart was updated.
             */
            if (
              !this.error &&
              this.submitButton.dataset
                .djQuantityLimitReached !==
                'true'
            ) {
              this.submitButton.removeAttribute(
                'aria-disabled'
              );
            }

            this.querySelector(
              '.loading__spinner'
            )?.classList.add('hidden');
          });
      }

      /*
       * ==========================================================
       * DJ QUANTITY VALIDATION
       * ==========================================================
       *
       * product-info.js calculates the live maximum that can
       * still be added from this product page.
       *
       * Example:
       *
       * Retail total max: 50
       * Already in cart: 20
       *
       * input.dataset.max = 50
       * input.max         = 30
       *
       * data-max therefore tells us the TOTAL limit for the
       * user-facing error message.
       *
       * max tells us how many can actually be submitted now.
       */
      validateQuantityLimit() {
        const input =
          this.quantityInput;

        if (!input) {
          return true;
        }

        const requestedQuantity =
          Number.parseInt(
            input.value,
            10
          );

        /*
         * Let Shopify handle unusual malformed requests if
         * there isn't an actual numeric quantity.
         */
        if (
          Number.isNaN(
            requestedQuantity
          )
        ) {
          return true;
        }

        /*
         * IMPORTANT:
         *
         * input.max is the REMAINING amount that may be added.
         *
         * It has already been calculated by product-info.js.
         */
        const inputMax =
          input.hasAttribute('max')
            ? Number.parseInt(
                input.getAttribute('max'),
                10
              )
            : null;

        /*
         * No maximum means there is nothing for this
         * custom validator to enforce.
         *
         * This is normally the case for exempt wholesale
         * products unless Shopify itself supplies a max.
         */
        if (
          inputMax === null ||
          Number.isNaN(inputMax)
        ) {
          return true;
        }

        /*
         * Quantity is valid.
         */
        if (
          requestedQuantity <= inputMax
        ) {
          return true;
        }

        /*
         * ========================================================
         * LIMIT EXCEEDED
         * ========================================================
         *
         * Do NOT submit the invalid quantity to Shopify.
         *
         * Instead:
         * 1. clamp the visible field to the valid maximum;
         * 2. show an informative error;
         * 3. focus/select the field;
         * 4. stop this submission.
         */

        input.value =
          Math.max(0, inputMax);

        /*
         * data-max contains the TOTAL product limit.
         *
         * Example:
         *
         * 20 already in cart
         * 30 remaining
         *
         * input.max         = 30
         * input.dataset.max = 50
         *
         * Therefore the user sees:
         *
         * "Maximum quantity of 50 reached."
         *
         * instead of the confusing:
         *
         * "Maximum quantity of 30 reached."
         */
        const totalMax =
          input.dataset.max
            ? Number.parseInt(
                input.dataset.max,
                10
              )
            : inputMax;

        const displayMax =
          Number.isNaN(totalMax)
            ? inputMax
            : totalMax;

        this.quantityLimitErrorShown =
          true;

        this.handleErrorMessage(
          `Maximum quantity of ${displayMax} reached.`
        );

        /*
         * Trigger input/change so the quantity component
         * immediately refreshes +/- button states.
         */
        input.dispatchEvent(
          new Event('input', {
            bubbles: true,
          })
        );

        input.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );

        /*
         * Restore our error after those events because
         * onQuantityInput() normally clears an old limit error.
         */
        this.quantityLimitErrorShown =
          true;

        this.handleErrorMessage(
          `Maximum quantity of ${displayMax} reached.`
        );

        input.focus();

        if (
          typeof input.select ===
          'function'
        ) {
          input.select();
        }

        return false;
      }

      /*
       * Clear our custom limit message when the customer
       * edits the quantity again.
       *
       * We only clear messages created by our validator;
       * unrelated Shopify errors remain untouched.
       */
      onQuantityInput() {
        if (
          !this.quantityLimitErrorShown
        ) {
          return;
        }

        this.quantityLimitErrorShown =
          false;

        this.handleErrorMessage();
      }

      /*
       * ==========================================================
       * DAWN ERROR MESSAGE
       * ==========================================================
       */
      handleErrorMessage(
        errorMessage = false
      ) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (
          !this.errorMessageWrapper
        ) {
          return;
        }

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        if (!this.errorMessage) {
          return;
        }

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }

      /*
       * ==========================================================
       * DAWN SUBMIT BUTTON
       * ==========================================================
       */
      toggleSubmitButton(
        disable = true,
        text
      ) {
        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (text) {
            this.submitButtonText.textContent =
              text;
          }
        } else {
          this.submitButton.removeAttribute(
            'disabled'
          );

          this.submitButtonText.textContent =
            window.variantStrings.addToCart;
        }
      }

      /*
       * Quantity input associated with this form.
       *
       * Using form.elements is important because Dawn's quantity
       * selector may be outside <product-form> and associated with
       * the form through:
       *
       * form="product-form-..."
       */
      get quantityInput() {
        const quantity =
          this.form?.elements?.namedItem(
            'quantity'
          );

        /*
         * namedItem can theoretically return a RadioNodeList.
         * The product quantity is normally a single input.
         */
        if (
          quantity instanceof
          HTMLInputElement
        ) {
          return quantity;
        }

        return null;
      }

      get variantIdInput() {
        return this.form.querySelector(
          '[name=id]'
        );
      }
    }
  );
}